import { appConfiguration } from '../appConfiguration.ts';


interface ResponseWrapper<Data> {
  success: boolean;
  response?: Data;
  message?: string;
}


interface PagedResults<Data> {
  next?: string | null;
  previous?: string | null;
  results: Data[];
}


// Identity details of a user, as defined by the Sample Tracker, and as returned by the UserIdentities API.
interface UserDto {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  groups: string[];
  date_joined: string;
}

interface UserBasicDto {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}


// Assemble a redirect to the login page specified in appConfiguration,
// with a return location of the current page, and send the browser to it.
function redirectToLogin() {
  const loginUrl = new URL(appConfiguration.config.authUrl);
  // This value is supposed to be stored in session data by the back end,
  // while it sends the user out to the OIDC service and back.
  // Then it's passed to the front end and used there to perform a final redirect.
  loginUrl.searchParams.append("process", "login");
  loginUrl.searchParams.append("next", window.location.toString());
  const url = loginUrl.toString();
  appConfiguration.logger(`Redirecting to '${url}'`);
  window.location.href = url;
}


// Return the Id of the currently authenticated user,
// or immediately redirect to the login page.
async function getLoggedInUser(): Promise<ResponseWrapper<UserDto>> {
  const result = await trackerGet('users/current/', {});
  if (!result.success) {
    redirectToLogin();
    return { success: false, message: result.message };
  }
  const record:any = await result.response!.json();
  return { success: true, response: record };
}


function getToken(): string | null {
  const token = sessionStorage.getItem("sessionid");
  if (token === null) {
    appConfiguration.logger("No token found in session storage");
    return null;
  }
  return token;
}


// A general-purpose function that constructs and makes an asynchronous API call.
async function trackerApiCall(url: string, method: string, params: Record<string, string> | null, body: string | null): Promise<ResponseWrapper<Response>> {

  appConfiguration.logger(`Tracker ${method} to '${url}':`);

  var headers:HeadersInit = { };
  const c = await cookieStore.get("csrftoken");
  const csrfToken = c ? c.value : null;
  if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
  }

  if (body) {
    headers['Content-Type'] = 'application/json';
  }

  var queryString = "";
  if (params) {
    const searchParams = new URLSearchParams(params);
    queryString = `?${searchParams}`;
    appConfiguration.logger(`Params: ${searchParams}`);
  }

  var requestInit: RequestInit = {
        method: method,
        headers: headers,
      };
  if (body) {
    requestInit['body'] = body;
    appConfiguration.logger(`Body: ${body}`);
  }

  try {
    const requestInfo: RequestInfo = new Request(`${appConfiguration.config.baseUrl}/${appConfiguration.config.apiPath}/${url}${queryString}`, requestInit );
    const response = await fetch(requestInfo);
    var result;

    if (response.status == 201 || response.status == 200 || response.status == 204) {
      result = { success: true, response: response };
    } else {
      if (response.status == 401) {
        result = { success: false, response: response, message: "Error: Unauthorized. Are you sure you're still logged in?" };
      } else if (response.status == 400) {
        result = { success: false, response: response, message: "Error: Bad request. Check your input. Are you sure you're still logged in?" };
      } else {
        result = { success: false, response: response, message: "Error" };
      }
    }

  } catch (err) {
    result = { success: false, message: "Exception" };
  }

  appConfiguration.logger(result);
  return result;
}


// A general-purpose function for making an asynchronous API call using the GET method.
async function trackerGet(url: string, params: Record<string, string>): Promise<ResponseWrapper<Response>> {
  return trackerApiCall(url, "GET", params, null);
}

// A general-purpose function for making an asynchronous API call using the DELETE method.
async function trackerDelete(url: string, params: Record<string, string>): Promise<ResponseWrapper<Response>> {
  return trackerApiCall(url, "DELETE", params, null);
}

// A general-purpose function for making an asynchronous API call using the PATCH method.
async function trackerPatch(url: string, body: string): Promise<ResponseWrapper<Response>> {
  return trackerApiCall(url, "PATCH", null, body);
}

// A general-purpose function for making an asynchronous API call using the POST method.
async function trackerPost(url: string, body: string): Promise<ResponseWrapper<Response>> {
  return trackerApiCall(url, "POST", null, body);
}


// Helper function to decode the JSON result of an API call,
// and re-wrap it in a ResponseWrapper with the same success status and message.
async function decodeResult<T>(result: ResponseWrapper<Response>): Promise<ResponseWrapper<T>> {
  if (!result.success) {
    return { success: false, message: result.message };
  }
  const record:any = await result.response!.json();
  return { success: true, response: record };
}


export type { ResponseWrapper, PagedResults, UserDto, UserBasicDto }
export { redirectToLogin, getLoggedInUser, trackerGet, trackerPost, trackerDelete, trackerPatch, decodeResult }
